import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { api } from '../api/client'

interface WorkspaceWithRole {
  workspaceID: number
  name: string
  ownerUserID: number
  efmEntityID?: number
  efmCategoryID?: number
  aggregateFollowers: number
  pricingTierID?: number
  autoLogToCRM: boolean
  createdAt: string
  userRole: string
  connectionCount: number
  memberCount: number
  isActive: boolean
}

interface WorkspaceContextValue {
  workspaces: WorkspaceWithRole[]
  activeWorkspace: WorkspaceWithRole | null
  role: string | null
  loading: boolean
  switchWorkspace: (wsId: number) => void
  refreshWorkspaces: () => Promise<void>
}

const STORAGE_KEY = 'smm_active_workspace_id'

const WorkspaceContext = createContext<WorkspaceContextValue>({
  workspaces: [],
  activeWorkspace: null,
  role: null,
  loading: true,
  switchWorkspace: () => {},
  refreshWorkspaces: async () => {},
})

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const [workspaces, setWorkspaces] = useState<WorkspaceWithRole[]>([])
  const [activeId, setActiveId] = useState<number | null>(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? parseInt(stored, 10) : null
  })
  const [loading, setLoading] = useState(true)

  const fetchWorkspaces = useCallback(async () => {
    try {
      const list = await api.get<WorkspaceWithRole[]>('/my-workspaces')
      const active = list.filter(w => w.isActive)
      setWorkspaces(active)

      if (active.length > 0) {
        const jwtWsId = getJwtWorkspaceId()
        const storedId = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10)

        const match = active.find(w => w.workspaceID === storedId)
          ?? active.find(w => w.workspaceID === jwtWsId)
          ?? active[0]

        setActiveId(match.workspaceID)
        localStorage.setItem(STORAGE_KEY, String(match.workspaceID))
      }
    } catch (err) {
      console.error('[SMM] Failed to fetch workspaces', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchWorkspaces() }, [fetchWorkspaces])

  const switchWorkspace = useCallback((wsId: number) => {
    setActiveId(wsId)
    localStorage.setItem(STORAGE_KEY, String(wsId))
  }, [])

  const activeWorkspace = workspaces.find(w => w.workspaceID === activeId) ?? null
  const role = activeWorkspace?.userRole ?? null

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <span style={{ fontSize: '14px', color: '#6b7280' }}>Loading workspace…</span>
      </div>
    )
  }

  return (
    <WorkspaceContext.Provider value={{
      workspaces,
      activeWorkspace,
      role,
      loading,
      switchWorkspace,
      refreshWorkspaces: fetchWorkspaces,
    }}>
      {children}
    </WorkspaceContext.Provider>
  )
}

export function useWorkspaceContext() {
  return useContext(WorkspaceContext)
}

export function useRole() {
  const { role } = useContext(WorkspaceContext)
  return {
    role,
    isAdmin: role === 'Admin',
    isEditor: role === 'Admin' || role === 'Editor',
    isContributor: role === 'Admin' || role === 'Editor' || role === 'Contributor',
    isViewer: role != null,
    canManageTeam: role === 'Admin',
    canManageConnections: role === 'Admin' || role === 'Editor',
    canPublish: role === 'Admin' || role === 'Editor' || role === 'Contributor',
    canComment: role != null,
  }
}

function getJwtWorkspaceId(): number | null {
  try {
    const token = localStorage.getItem('smm_token')
    if (!token) return null
    const payload = JSON.parse(atob(token.split('.')[1]))
    const wsId = parseInt(payload.workspace_id, 10)
    return Number.isFinite(wsId) && wsId > 0 ? wsId : null
  } catch { return null }
}
